import {
  Link,
  Text,
  Wrap,
  Box,
} from '@chakra-ui/react';
import {
  useHeavyTrafficLaneKm,
  useHeavyVKT,
  useHeavyVKTPercentage,
} from '@src/modules/performance/context/reports-api-hooks';
import Chart from 'react-apexcharts';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import GroupedRegionMessage from '@src/modules/performance/components/GroupedRegionMessage';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import StackedBarChart from '@src/modules/performance/components/charts/StackedBarChart';
import LockYearBadge from '@src/modules/performance/components/LockYearBadge';
import { useRecoilValue } from 'recoil';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Heavy Vehicle Road Use',
  content: [
    {
      title: 'Heavy vehicle kilometres travelled',
      description:
  <Text fontSize="inherit" mb={2}>
    Estimated total kilometres travelled by heavy vehicles on the selected road network per year. The totals are based on the percentage of heavy vehicles calculated by the traffic count estimation for the network.
  </Text>,
      source: null,
    },
    {
      title: 'Heavy Vehicle Traffic per Lane km',
      description:
  <Text fontSize="inherit" mb={2}>
    Estimated kilometres travelled by heavy vehicles divided by the total lane kilometre length, for the selected road network per year.
    For RCAs, the chart also shows the average estimated vkt per lane km of the RCA’s peer group.
    The chart also shows the average estimated vkt per lane km for the National road network.
  </Text>,
      source: null,
    },
    {
      title: '% Heavy VKT',
      description:
  <Text fontSize="inherit" mb={2}>
    The percentage of estimated total vehicle traffic (in kilometres travelled) that is by heavy vehicles on the selected road network per year.
    {' '}
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from AWM and
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools" isExternal>NZTA, vehicle use</Link>
  </Text>,
    },
  ],
};

function RoadNetworkUseHeavyVehicleRoadUse() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: heavyTrafficLaneKmData, isLoading: isLoadingHeavyTrafficLaneKm } = useHeavyTrafficLaneKm();
  const { data: heavyVktData, isLoading: isLoadingHeavyVkt } = useHeavyVKT();
  const { data: heavyVktPercentageData, isLoading: isLoadingHeavyVktPercentage } = useHeavyVKTPercentage();
  const isLoading = isLoadingHeavyTrafficLaneKm || isLoadingHeavyVkt || isLoadingHeavyVktPercentage;
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  // Check if all lockYears are the same
  const allLockYears = [
    heavyTrafficLaneKmData?.[0]?.lockYear,
    heavyVktData?.[0]?.LockYear,
    heavyVktPercentageData?.[0]?.LockYear,
  ];
  const primaryLockYear = allLockYears.every((year) => year === allLockYears[0]) ? allLockYears[0] : null;

  // Heavy VKT chart data
  const heavyVktYears = [...new Set(heavyVktData?.map((item) => item.Year))];
  const heavyVktChartData = [
    {
      name: 'Urban',
      data: heavyVktData
        ?.filter((x) => x.RoadType === 'U' || x.RoadType === 'Urban')
        .map((item) => ({
          x: item.Year,
          y: item.HeavyVKT / 1000000,
        })),
      color: CHART_COLOURS.blue,
    },
    {
      name: 'Rural',
      data: heavyVktData
        ?.filter((x) => x.RoadType === 'R' || x.RoadType === 'Rural')
        .map((item) => ({
          x: item.Year,
          y: item.HeavyVKT / 1000000,
        })),
      color: CHART_COLOURS.red,
    },
  ];

  // Heavy VKT Percentage chart data
  const heavyVktPercentageYears = [...new Set(heavyVktPercentageData?.map((item) => item.Year))];
  const heavyVktPercentageChartData = [
    {
      name: '% Heavy VKT',
      type: 'line',
      data: heavyVktPercentageData?.map((item) => ({
        x: item.Year,
        y: item.HeavyVKTPercent,
      })),
      color: CHART_COLOURS.blue,
    },
  ];

  // Heavy Traffic Lane Km chart data
  const heavyTrafficLaneKmYears = [...new Set(heavyTrafficLaneKmData?.map((item) => item.year))];
  const heavyTrafficLaneKmChartData = [
    {
      name: currentDatabase?.name,
      data: heavyTrafficLaneKmData?.filter((x) => x.networkOrder === 1).map((item) => ({
        x: item.year,
        y: Math.round(item.vktPerLaneKm),
      })),
      color: CHART_COLOURS.blue,
    },
    ...(!isNational
      ? [
        {
          name: 'National',
          data: heavyTrafficLaneKmData
            ?.filter((x) => x.networkOrder === 3)
            .map((item) => ({
              x: item.year,
              y: Math.round(item.vktPerLaneKm),
            })),
          color: CHART_COLOURS.lightgrey,
        },
      ]
      : []),
    {
      name: 'Peer Group',
      data: heavyTrafficLaneKmData?.filter((x) => x.networkOrder === 2).map((item) => ({
        x: item.year,
        y: Math.round(item.vktPerLaneKm),
      })),
      color: CHART_COLOURS.black,
    },
  ];

  // Filter out the Peer Group and National data if it's a national report, and Peer Group if it's a region or state highways report
  const heavyTrafficLaneKmChartSeries = filterChartDataByName(
    heavyTrafficLaneKmChartData,
    chartFilters,
  );

  return (
    <ReportSection
      title="Heavy Vehicle Road Use"
      isLoading={isLoading || !heavyVktData || !heavyVktPercentageData || !heavyTrafficLaneKmData}
      informationSource={informationSource}
      lockYear={primaryLockYear || null}
    >
      <Wrap spacing={0}>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={heavyVktData?.[0]?.LockYear}
          />
          )}
          <StackedBarChart
            title="Heavy vehicle kilometres travelled"
            categories={heavyVktYears}
            series={heavyVktChartData}
            width="100%"
            height="280"
            prefix=""
            suffix="M"
            decimalPoints={0}
            yAxisDecimalPoints={0}
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={heavyTrafficLaneKmData?.[0]?.lockYear}
          />
          )}
          <Chart
            options={{
              title: {
                text: 'Heavy Vehicle Traffic per Lane km',
              },
              chart: {
                animations: {
                  enabled: false,
                },
              },
              xaxis: {
                categories: heavyTrafficLaneKmYears,
                labels: {
                  offsetY: 5,
                  rotate: -45,
                  rotateAlways: true,
                },
              },
              yaxis: [
                {
                  title: {
                    text: 'Heavy VKT per lane km',
                    offsetX: 5,
                  },
                  labels: {
                    formatter(y) {
                      return `${y.toFixed(0)}`;
                    },
                  },
                },
              ],
              grid: {
                padding: {
                  left: 10,
                },
              },
              dataLabels: {
                enabled: false,
              },
              tooltip: {
                shared: true,
                y: {
                  formatter: (val) => `${val.toFixed(0)}`,
                },
              },
            }}
            series={heavyTrafficLaneKmChartSeries}
            type="line"
            width="100%"
            height="280"
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={heavyVktPercentageData?.[0]?.LockYear}
          />
          )}
          <Chart
            options={{
              title: {
                text: '% Heavy VKT',
              },
              chart: {
                animations: {
                  enabled: false,
                },
              },
              xaxis: {
                categories: heavyVktPercentageYears,
                labels: {
                  offsetY: 5,
                  rotate: -45,
                  rotateAlways: true,
                },
              },
              yaxis: [
                {
                  forceNiceScale: false,
                  labels: {
                    formatter(y) {
                      return `${y.toFixed(1)}%`;
                    },
                  },
                },
              ],
              grid: {
                padding: {
                  left: 10,
                },
              },
              dataLabels: {
                enabled: false,
              },
              tooltip: {
                shared: true,
                intersect: false,
                y: {
                  formatter: (val) => `${val.toFixed(1)}%`,
                },
              },
            }}
            series={heavyVktPercentageChartData}
            type="line"
            width="100%"
            height="280"
          />
        </Box>
      </Wrap>
      <GroupedRegionMessage rcaIds={[1014, 1015, 1016]} />
    </ReportSection>
  );
}

export default RoadNetworkUseHeavyVehicleRoadUse;
